import React, { useState, useMemo } from 'react';
import { PageShell, StatCard, DataTable } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { escapeHtml } from '../../utils/escapeHtml';

const MODALITIES = ['X-Ray', 'Ultrasound (USG)', 'CT Scan', 'MRI', 'Mammography', 'DEXA Scan', 'Fluoroscopy', 'Echo', 'Doppler', 'PET Scan', 'Nuclear Medicine'];
const CONTRAST = ['Without Contrast', 'With Contrast (IV)', 'With Contrast (Oral)', 'With Contrast (IV + Oral)'];
const REPORT_TEMPLATES = {
  'X-Ray': 'CHEST X-RAY REPORT\n\nFindings:\n\nImpression:\n1. ',
  'Ultrasound (USG)': 'ULTRASONOGRAPHY REPORT\n\nFindings:\n\nImpression:\n1. ',
  'CT Scan': 'CT SCAN REPORT\n\nFindings:\n\nImpression:\n1. ',
  MRI: 'MRI REPORT\n\nFindings:\n\nImpression:\n1. ',
};

const STATUS_UI = {
  pending: 'Pending',
  in_progress: 'In Progress',
  reported: 'Reported',
  verified: 'Verified',
};

function uiStatus(s) {
  return STATUS_UI[s] || s || 'Pending';
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read image'));
    reader.readAsDataURL(file);
  });
}

function NewOrderTab({ onDone, user }) {
  const emptyForm = {
    patient: '', uhid: '', age: '', gender: 'M', modality: 'X-Ray', study: '',
    contrast: 'Without Contrast', clinicalInfo: '', referredBy: user?.name || '', priority: 'Routine', amount: '',
    admissionId: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const { data: admData } = useLiveData(() => ipApi.getAdmissions(80), []);
  const admissions = admData?.admissions || [];

  function pickAdmission(admId) {
    if (!admId) { setForm((f) => ({ ...f, admissionId: '' })); return; }
    const adm = admissions.find((a) => a.admissionId === admId);
    if (!adm) return;
    setForm((f) => ({
      ...f,
      admissionId: adm.admissionId,
      patient: adm.patientName || f.patient,
      uhid: adm.patientId || adm.uhid || f.uhid,
      age: adm.age != null ? String(adm.age) : f.age,
      gender: adm.gender || f.gender,
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await ipApi.createRadiologyOrder({
        patientName: form.patient,
        patientId: form.uhid,
        age: form.age,
        gender: form.gender,
        modality: form.modality,
        study: form.study,
        contrast: form.contrast,
        clinicalInfo: form.clinicalInfo,
        referredBy: form.referredBy,
        priority: form.priority,
        amount: Number(form.amount) || 0,
        admissionId: form.admissionId || undefined,
        hospitalId: user?.hospitalId,
      });
      setMsg(`Order placed for ${form.patient}.`);
      setForm({ ...emptyForm, referredBy: user?.name || '' });
      onDone?.();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit} style={{ borderLeft: '4px solid #6366f1' }}>
      <h3 style={{ marginTop: 0, color: '#6366f1' }}>New radiology order</h3>
      {msg && <div className="info-banner mb">{msg}</div>}
      {err && <div className="error mb">{err}</div>}
      {admissions.length > 0 && (
        <div className="field mb">
          <label>Pick inpatient (auto-fills fields)</label>
          <select className="input" value={form.admissionId} onChange={(e) => pickAdmission(e.target.value)}>
            <option value="">— Select IP patient or fill manually —</option>
            {admissions.map((adm) => (
              <option key={adm.admissionId} value={adm.admissionId}>
                {adm.patientName} · {adm.ward} Bed {adm.bedLabel || adm.bedId} · {adm.admissionId}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="form-grid mb">
        <div className="field"><label>Patient name *</label><input className="input" required value={form.patient} onChange={(e) => setForm((f) => ({ ...f, patient: e.target.value }))} /></div>
        <div className="field"><label>UHID</label><input className="input" value={form.uhid} onChange={(e) => setForm((f) => ({ ...f, uhid: e.target.value }))} /></div>
        <div className="field"><label>Age</label><input type="number" className="input" value={form.age} onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))} /></div>
        <div className="field"><label>Gender</label><select className="input" value={form.gender} onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}><option value="M">Male</option><option value="F">Female</option><option value="O">Other</option></select></div>
        <div className="field"><label>Modality *</label><select className="input" required value={form.modality} onChange={(e) => setForm((f) => ({ ...f, modality: e.target.value }))}>{MODALITIES.map((m) => <option key={m}>{m}</option>)}</select></div>
        <div className="field"><label>Study *</label><input className="input" required value={form.study} onChange={(e) => setForm((f) => ({ ...f, study: e.target.value }))} /></div>
        <div className="field"><label>Contrast</label><select className="input" value={form.contrast} onChange={(e) => setForm((f) => ({ ...f, contrast: e.target.value }))}>{CONTRAST.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div className="field"><label>Priority</label><select className="input" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}><option>Routine</option><option>Urgent</option><option>Emergency</option><option>STAT</option></select></div>
        <div className="field"><label>Referred by</label><input className="input" value={form.referredBy} onChange={(e) => setForm((f) => ({ ...f, referredBy: e.target.value }))} /></div>
        <div className="field"><label>Amount (₹)</label><input type="number" className="input" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} /></div>
        <div className="field full"><label>Clinical indication *</label><textarea className="input" rows={3} required value={form.clinicalInfo} onChange={(e) => setForm((f) => ({ ...f, clinicalInfo: e.target.value }))} /></div>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Placing…' : 'Place order'}</button>
      </div>
    </form>
  );
}

function printRadiologyReport({ order, report, impression, radiologist }) {
  const win = window.open('', '_blank', 'width=800,height=700');
  if (!win) return;
  const date = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  win.document.write('<html><head><title>Radiology Report</title><style>');
  win.document.write('body{font-family:Arial,sans-serif;max-width:720px;margin:30px auto;color:#1e293b}');
  win.document.write('.header{border-bottom:2px solid #6366f1;padding-bottom:12px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-start}');
  win.document.write('h2{margin:0;color:#6366f1;font-size:18px}.meta{font-size:12px;color:#64748b}.section{margin:14px 0}.section label{font-weight:700;font-size:11px;text-transform:uppercase;color:#6366f1;display:block;margin-bottom:4px}');
  win.document.write('.report-body{white-space:pre-wrap;font-family:monospace;font-size:13px;background:#f8fafc;padding:12px;border-radius:6px;border:1px solid #e2e8f0}');
  win.document.write('.impression{background:#fffbeb;padding:12px;border-radius:6px;border-left:4px solid #f59e0b;white-space:pre-wrap}');
  win.document.write('.sig{margin-top:40px;border-top:1px solid #e2e8f0;padding-top:14px;font-size:12px}');
  win.document.write('@media print{body{margin:10px}}');
  win.document.write('</style></head><body>');
  win.document.write('<div class="header"><div><h2>Radiology Report</h2><div class="meta">' + escapeHtml(order?.study || '') + ' · ' + escapeHtml(order?.modality || '') + ' · ' + escapeHtml(order?.contrast || '') + '</div></div><div style="text-align:right;font-size:12px"><div><strong>' + escapeHtml(date) + '</strong></div><div style="color:#64748b">Order: ' + escapeHtml(order?.orderId || '') + '</div></div></div>');
  win.document.write('<div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;font-size:13px">');
  win.document.write('<div><strong>Patient:</strong> ' + escapeHtml(order?.patientName || '') + '</div>');
  win.document.write('<div><strong>UHID:</strong> ' + escapeHtml(order?.patientId || '') + '</div>');
  win.document.write('<div><strong>Age / Gender:</strong> ' + escapeHtml(String(order?.age || '')) + 'y / ' + escapeHtml(order?.gender || '') + '</div>');
  win.document.write('<div><strong>Referred by:</strong> ' + escapeHtml(order?.referredBy || '') + '</div>');
  win.document.write('<div><strong>Priority:</strong> ' + escapeHtml(order?.priority || '') + '</div>');
  win.document.write('<div><strong>Clinical info:</strong> ' + escapeHtml(order?.clinicalInfo || '') + '</div>');
  win.document.write('</div>');
  win.document.write('<div class="section"><label>Report</label><div class="report-body">' + escapeHtml(report || '') + '</div></div>');
  if (impression) {
    win.document.write('<div class="section"><label>Impression / Conclusion</label><div class="impression">' + escapeHtml(impression) + '</div></div>');
  }
  win.document.write('<div class="sig"><strong>Radiologist:</strong> ' + escapeHtml(radiologist || '') + '<br/><div style="margin-top:28px;border-top:1px dashed #94a3b8;width:200px;padding-top:4px;font-size:11px;color:#64748b">Signature &amp; Stamp</div></div>');
  win.document.write('</body></html>');
  win.document.close();
  win.print();
}

function ReportEntryTab({ order, onBack, onSaved }) {
  const { user } = useAuth();
  const [report, setReport] = useState(order.report || REPORT_TEMPLATES[order?.modality] || '');
  const [impression, setImpression] = useState(order.impression || '');
  const [radiologist, setRadiologist] = useState(order.reportedBy || user?.name || '');
  const [images, setImages] = useState(Array.isArray(order.images) ? order.images : []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [lightbox, setLightbox] = useState(null);

  async function onPickImages(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setErr('');
    try {
      const next = [...images];
      for (const file of files.slice(0, 8 - next.length)) {
        if (!String(file.type || '').startsWith('image/')) {
          setErr('Only image files (JPEG/PNG/WebP) are supported.');
          continue;
        }
        if (file.size > 4 * 1024 * 1024) {
          setErr(`${file.name} is too large (max 4 MB per image).`);
          continue;
        }
        const dataUrl = await readFileAsDataUrl(file);
        next.push({
          name: file.name,
          url: dataUrl,
          mimeType: file.type,
          uploadedAt: new Date().toISOString(),
        });
      }
      setImages(next.slice(0, 8));
    } catch (ex) {
      setErr(ex.message || 'Could not attach image');
    }
  }

  async function saveReport(status) {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      await ipApi.patchRadiologyOrder(order.orderId, {
        report,
        impression,
        reportedBy: radiologist,
        images,
        status,
      });
      setMsg(status === 'verified' ? 'Report verified.' : 'Report saved.');
      await onSaved?.();
      if (status === 'verified') onBack?.();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ borderLeft: '4px solid #10b981' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: '#10b981' }}>Report — {order?.study}</h3>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
      </div>
      <div className="info-banner mb" style={{ background: '#fffbeb', borderColor: '#fde68a', color: '#78350f' }}>
        Full DICOM/PACS not included — JPEG study images supported. Attach up to 8 images (max 4 MB each); click to enlarge.
      </div>
      {msg && <div className="info-banner mb">{msg}</div>}
      {err && <div className="error mb">{err}</div>}

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
        >
          <img src={lightbox.url} alt={lightbox.name} style={{ maxWidth: '92vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} onClick={(e) => e.stopPropagation()} />
          <button
            type="button"
            onClick={() => setLightbox(null)}
            style={{ position: 'absolute', top: 16, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', borderRadius: 999, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >✕</button>
          <div style={{ position: 'absolute', bottom: 16, color: '#e2e8f0', fontSize: 12 }}>{lightbox.name}</div>
        </div>
      )}

      <div className="field mb"><label>Radiologist</label><input className="input" required value={radiologist} onChange={(e) => setRadiologist(e.target.value)} /></div>
      <div className="field mb">
        <label>Study images ({images.length}/8) — max 4 MB each, JPEG/PNG/WebP</label>
        <input type="file" accept="image/*" multiple onChange={onPickImages} />
        {images.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(120px,1fr))', gap: 8, marginTop: 10 }}>
            {images.map((img, i) => (
              <div key={`${img.name}-${i}`} style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#0f172a' }}>
                <img
                  src={img.url}
                  alt={img.name}
                  title="Click to enlarge"
                  style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                  onClick={() => setLightbox(img)}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4, padding: 6 }}>
                  <span style={{ fontSize: 10, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.name}</span>
                  <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => setImages((arr) => arr.filter((_, j) => j !== i))}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="field mb"><label>Report body</label><textarea className="input" rows={14} value={report} onChange={(e) => setReport(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 13 }} /></div>
      <div className="field mb"><label>Impression (summary)</label><textarea className="input" rows={3} value={impression} onChange={(e) => setImpression(e.target.value)} placeholder="Key impression for clinicians" /></div>
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={() => printRadiologyReport({ order, report, impression, radiologist })}>Print report</button>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => saveReport('reported')}>{busy ? 'Saving…' : 'Save report'}</button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => saveReport('verified')}>{busy ? 'Saving…' : 'Save & verify'}</button>
      </div>
    </div>
  );
}

function TatAnalyticsTab({ orders }) {
  const reported = orders.filter((o) => ['reported', 'verified'].includes(o.status));
  const byModality = MODALITIES.slice(0, 6).map((m) => {
    const rows = orders.filter((o) => o.modality === m);
    const done = rows.filter((o) => ['reported', 'verified'].includes(o.status));
    const tatMins = done.map((o) => {
      if (!o.createdAt || !o.reportedAt) return null;
      return Math.round((new Date(o.reportedAt) - new Date(o.createdAt)) / 60000);
    }).filter((x) => x != null && x >= 0);
    const avg = tatMins.length ? Math.round(tatMins.reduce((a, b) => a + b, 0) / tatMins.length) : null;
    return { modality: m, count: rows.length, reported: done.length, avgTat: avg };
  });

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Modality performance (live)</h3>
      <p className="hint mb">{reported.length} reported/verified studies in current list.</p>
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Modality</th><th>Orders</th><th>Reported</th><th>Avg TAT (min)</th></tr></thead>
          <tbody>
            {byModality.map((m) => (
              <tr key={m.modality}>
                <td>{m.modality}</td>
                <td>{m.count}</td>
                <td>{m.reported}</td>
                <td>{m.avgTat != null ? m.avgTat : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RadiologyPage() {
  const { user } = useAuth();
  const { data, loading, error, reload } = useLiveData(() => ipApi.getRadiologyOrders(80), []);
  const orders = useMemo(() => (data?.orders || []).map((o) => ({ ...o, id: o.orderId, statusUi: uiStatus(o.status) })), [data]);
  const [tab, setTab] = useState('worklist');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [actionErr, setActionErr] = useState('');

  const filtered = statusFilter === 'All' ? orders : orders.filter((o) => uiStatus(o.status) === statusFilter);
  const pColor = { Urgent: '#f97316', Emergency: '#dc2626', Routine: '#10b981', STAT: '#dc2626' };
  const sColor = { Pending: '#f59e0b', 'In Progress': '#3b82f6', Reported: '#10b981', Verified: '#6366f1' };

  const columns = [
    { key: 'orderId', label: 'Order ID', render: (r) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6366f1', fontWeight: 700 }}>{r.orderId}</span> },
    { key: 'patient', label: 'Patient', render: (r) => <div><div style={{ fontWeight: 700 }}>{r.patientName}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{r.patientId} · {r.age}y/{r.gender}</div></div> },
    { key: 'modality', label: 'Modality', render: (r) => <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#f1f5f9' }}>{r.modality}</span> },
    { key: 'study', label: 'Study', render: (r) => <div><div style={{ fontWeight: 600, fontSize: 13 }}>{r.study}</div><div style={{ fontSize: 11, color: '#64748b' }}>{r.referredBy}</div></div> },
    { key: 'images', label: 'Images', render: (r) => (Array.isArray(r.images) && r.images.length ? `${r.images.length}` : '—') },
    { key: 'priority', label: 'Priority', render: (r) => <span style={{ fontSize: 11, fontWeight: 800, color: pColor[r.priority] || '#94a3b8' }}>{r.priority}</span> },
    { key: 'status', label: 'Status', render: (r) => <span style={{ fontSize: 11, fontWeight: 700, color: sColor[r.statusUi] || '#64748b' }}>{r.statusUi}</span> },
    { key: 'actions', label: 'Actions', render: (r) => (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {r.status === 'pending' && (
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={async () => {
              setActionErr('');
              try {
                await ipApi.patchRadiologyOrder(r.orderId, { status: 'in_progress' });
                await reload();
              } catch (e) {
                setActionErr(e.message || 'Could not start study');
              }
            }}
          >
            Start
          </button>
        )}
        {r.status !== 'verified' && (
          <button type="button" className="btn btn-sm btn-primary" onClick={() => { setSelectedOrder(r); setTab('report'); }}>
            {['reported', 'verified'].includes(r.status) ? 'View / edit' : 'Report'}
          </button>
        )}
        {r.status === 'verified' && (
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setSelectedOrder(r); setTab('report'); }}>View</button>
        )}
      </div>
    )},
  ];

  return (
    <PageShell
      title="Radiology / RIS"
      subtitle="Live worklist · orders · image attach · reporting · TAT"
      actions={<button type="button" className="btn btn-secondary btn-sm" onClick={reload}>Refresh</button>}
    >
      <div className="stat-grid">
        <StatCard label="Total" value={orders.length} accent="#6366f1" loading={loading} />
        <StatCard label="Pending" value={orders.filter((o) => o.status === 'pending').length} accent="#f59e0b" loading={loading} />
        <StatCard label="In progress" value={orders.filter((o) => o.status === 'in_progress').length} accent="#3b82f6" loading={loading} />
        <StatCard label="Reported" value={orders.filter((o) => ['reported', 'verified'].includes(o.status)).length} accent="#10b981" loading={loading} />
      </div>

      {(error || actionErr) && <div className="error mb">{error || actionErr}</div>}

      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'worklist', label: 'Worklist' }, { id: 'new', label: '+ New order' }, { id: 'analytics', label: 'TAT analytics' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => { setTab(id); setSelectedOrder(null); }}>{label}</button>
        ))}
      </div>

      {tab === 'report' && selectedOrder && (
        <ReportEntryTab order={selectedOrder} onBack={() => setTab('worklist')} onSaved={reload} />
      )}

      {tab === 'worklist' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {['All', 'Pending', 'In Progress', 'Reported', 'Verified'].map((s) => (
              <button key={s} type="button" className={`emr-chip ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
                {s} {s === 'All' ? `(${orders.length})` : `(${orders.filter((o) => uiStatus(o.status) === s).length})`}
              </button>
            ))}
          </div>
          <DataTable columns={columns} rows={filtered} keyField="orderId" loading={loading} emptyMessage="No radiology orders yet." />
        </>
      )}
      {tab === 'new' && <NewOrderTab user={user} onDone={() => { reload(); setTab('worklist'); }} />}
      {tab === 'analytics' && <TatAnalyticsTab orders={orders} />}
    </PageShell>
  );
}
